import { DisplayString } from '@coretava/i18n';
import CoretavaLogo from '@gamiphy/icons/CoretavaLogo';
import PoweredByCoretava from '@gamiphy/icons/PoweredByCoretava';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import { AppWelcomeGiftStore } from '@gamiphy/loyalty-station-desktop/store/appWelcomeGift';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { DisplayStringTrans, Trans } from '@gamiphy/react-i18n';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { formatCashbackPromotionValue, formatWelcomeGiftValue } from '../../utils';
import defaultBackground from './assets/background.jpg';
import Benefit from './Benefit';
import { useBenefits } from './hooks';

const NonLoggedIn: React.FC<{ setStarted: (started: boolean) => void }> = ({ setStarted }) => {
  const benefits = useBenefits();
  const title = React.useMemo(() => {
    if (AppWelcomeGiftStore.welcomeGift?.enabled) {
      return (
        <Trans
          i18nKey="coreloyalty.cashback.discount.waiting"
          defaults="A {{welcomeGiftValue}} discount is waiting for you!"
          values={{
            welcomeGiftValue: formatWelcomeGiftValue(AppWelcomeGiftStore.welcomeGift),
          }}
        />
      );
    }
    if (AppStore.app.agent === 'wake') {
      return (
        <Trans
          i18nKey="coreloyalty.cashback.getCashbacks.predefinedPromotions"
          defaults="Enjoy up to {{promotionValue}} cashback on every purchase you make!"
          values={{
            promotionValue: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
          }}
        />
      );
    }
    return (
      <Trans
        i18nKey="coreloyalty.cashback.getCashbacks"
        defaults="Enjoy a {{cashbackPercentage}}% cashback on every purchase you make!"
        values={{
          cashbackPercentage: AppCashbackStore.defaultAppCashback?.percentage,
        }}
      />
    );
  }, [AppWelcomeGiftStore.welcomeGift?.enabled,
    AppCashbackStore.defaultAppCashback?.percentage,
    AppCashbackStore.highestPromotionAppCashback,
    AppStore.app,
  ]);

  return (
    <Box height="100%" display="flex" flexDirection="column" position="relative">
      <Box
        position="absolute"
        bgcolor="primary.main"
        color="text.secondary"
        display="flex"
        alignItems="center"
        left={0}
        top={0}
        width="100%"
        p={1}
        textAlign="center"
      >
        <Typography fontSize={16} sx={{ ml: 'auto', pl: 3 }}>
          {AppStore.app.name}
        </Typography>
        <IconButton onClick={() => UiStore.close()} sx={{ ml: 'auto' }}>
          <Close sx={{ color: 'text.secondary' }} />
        </IconButton>
      </Box>
      <Box
        flex={1}
        sx={{
          background: `url(${ThemeStore.theme.onboarding.landing?.images?.[0] || defaultBackground}), #E2E2E2`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Box
        bgcolor="#fff"
        borderRadius="32px 32px 0 0"
        mt="-32px"
        px={2}
        pt={2}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Box>
          {AppStore.app.logo ? <Box component="img" src={AppStore.app.logo} maxHeight={60} /> : <CoretavaLogo height={60} textColor="#000" />}
        </Box>
        <Typography fontSize={22} fontWeight={800} lineHeight={1.25} mb={0.5}>
          {title}
        </Typography>
        {!!benefits.length && (
          <>
            <Typography fontSize="1.6rem" fontWeight={400}>
              <Trans
                i18nKey="coreloyalty.cashback.benefits.whatElse"
                defaults="What else will you get?"
              />
            </Typography>
            <Stack spacing={1}>
              {benefits.map((benefit) => (
                <Benefit
                  key={uuidv4()}
                  icon={benefit.icon}
                  text={benefit.text}
                />
              ))}
            </Stack>
          </>
        )}
        <Box width="100%">
          <Button
            variant="contained"
            fullWidth
            onClick={() => setStarted(true)}
            sx={{
              mb: 1,
            }}
          >
            <DisplayStringTrans displayString={ThemeStore.theme?.onboarding.landing?.button as DisplayString} />

          </Button>
        </Box>
        {!AppStore.app.featureFlags.hidePoweredByCoretava && (
          <Box textAlign="center" height={49} display="flex" alignItems="center" justifyContent="center">
            <PoweredByCoretava color="#ADADAD" textColor="#ADADAD" width={176} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default observer(NonLoggedIn);
